/**
 * Reportes · Movimientos — libro completo de movimientos del sistema.
 * Read-only. Estado SNT derivado vía derivarEstadoTrazaMov.
 */

import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useMovimientos, useOperaciones, useUbicaciones } from '@/api/queries';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/format';
import { derivarEstadoTrazaMov } from '@/domain/traza';
import type { EstadoTrazaMov } from '@/domain/types';

const TRAZA_LABEL: Record<EstadoTrazaMov, string> = {
  pendiente: 'Pendiente',
  enviada: 'Enviada',
  error: 'Error',
  cancelada: 'Cancelada',
};
const TRAZA_BADGE: Record<EstadoTrazaMov, string> = {
  pendiente: 'badge-blue',
  enviada: 'badge-green',
  error: 'badge-red',
  cancelada: 'badge-gray',
};

export function MovimientosTab() {
  const { data: movimientos = [], isLoading } = useMovimientos();
  const { data: operaciones = [] } = useOperaciones();
  const { data: ubicaciones = [] } = useUbicaciones();

  const ordenados = useMemo(
    () => [...movimientos].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()),
    [movimientos],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando...
      </div>
    );
  }
  if (ordenados.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-gray-600">
          Sin movimientos registrados todavía.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="overflow-x-auto rounded border border-gray-100">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Serie</TableHead>
                <TableHead>Evento</TableHead>
                <TableHead>Op</TableHead>
                <TableHead>Ubicación</TableHead>
                <TableHead>Estado SNT</TableHead>
                <TableHead>Cód. Tx</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordenados.slice(0, 200).map((m) => {
                const op = operaciones.find((o) => o.id === m.operacionId);
                const traza = derivarEstadoTrazaMov(m, op);
                const ub = ubicaciones.find((u) => u.id === m.ubicacionId);
                return (
                  <TableRow key={m.id}>
                    <TableCell className="font-mono text-[11px]">
                      {fmtDateTime(m.fecha)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{m.serieId}</TableCell>
                    <TableCell className="text-xs">{m.tipoEvento}</TableCell>
                    <TableCell className="font-mono text-[11px]">
                      {m.operacionId ?? '—'}
                    </TableCell>
                    <TableCell className="text-xs">{ub?.nombre ?? '—'}</TableCell>
                    <TableCell>
                      <span className={cn('badge', TRAZA_BADGE[traza])}>
                        {TRAZA_LABEL[traza]}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-gray-600">
                      {m.codigoTransaccion ?? '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="mt-2 text-[11px] text-gray-600">
          Mostrando {Math.min(200, ordenados.length)} de {ordenados.length} movimientos.
        </div>
      </CardContent>
    </Card>
  );
}
